import React, { useEffect, useState } from "react";
import {
    X,
    AlertCircle,
    Calendar,
    Clock,
    Info,
    Send,
    FileText
} from "lucide-react";
import sidebarImage from "../../../assests/SidebarImage.png";
import SidebarHeaderImage from "../../../assests/SidebarHeaderImage.png";

const initialForm = {
    thankYouNote: "",
    startDate: "",
    startTime: "",
    startImmediately: false,
    endDate: "",
    endTime: "",
    partATime: "",
    partBTime: "",
    respondentFile: null,
};
const getTodayDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const PublishSurveyModal = ({ isOpen, survey, onClose, onPublish }) => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setForm(initialForm);
            setErrors({});
        }
    }, [isOpen]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "auto";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    if (!survey) return null;
    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const handlePublish = async () => {
        const today = getTodayDateString();
        const effectiveStartDate = form.startImmediately ? today : form.startDate;

        const nextErrors = {
            thankYouNote: form.thankYouNote.trim() ? "" : "Thank you note is required",
            startDate: !form.startImmediately && !form.startDate ? "Start date is required"
                : !form.startImmediately && form.startDate < today
                    ? "Start date cannot be in the past"
                    : "",
            startTime: !form.startImmediately && !form.startTime ? "Start time is required" : "",
            endDate: !form.endDate ? "End date is required" : form.endDate < effectiveStartDate
                ? "End date cannot be earlier than the start date"
                : "",
            endTime: !form.endTime ? "End time is required" : "",
            partATime: !form.partATime ? "Part A time is required" : "",
            partBTime: !form.partBTime ? "Part B time is required" : "",
        };

        if (Object.values(nextErrors).some(Boolean)) {
            setErrors(nextErrors);
            return;
        }
        try {
            const buildDateTime = (dateStr, timeStr) => `${dateStr}T${timeStr}`;
            

            const nowAsLocalDateTime = () => {
                const now = new Date();
                const hh = String(now.getHours()).padStart(2, "0");
                const mm = String(now.getMinutes()).padStart(2, "0");
                const ss = String(now.getSeconds()).padStart(2, "0");
                return `${today}T${hh}:${mm}:${ss}`;
            };

            const payload = {
                assessmentUID: survey.uniqueKey,
                thankYouNote: form.thankYouNote.trim(),
                startDateTime: form.startImmediately ? nowAsLocalDateTime() : buildDateTime(form.startDate, form.startTime),
                endDateTime: buildDateTime(form.endDate, form.endTime),
                partATime: Number(form.partATime),
                partBTime: Number(form.partBTime),
            };
            setSubmitting(true);
            await onPublish?.(survey, payload);
            onClose();
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || error.message || "Unable to publish assessment.");
        }
        finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] transition-opacity duration-300 z-[10050] ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                    }`}
            />

            <div
                className={`fixed top-0 right-0 h-full w-full md:w-[760px] bg-white shadow-2xl z-[10051] rounded-l-2xl overflow-hidden transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                <div className="relative overflow-hidden flex items-center justify-between px-6 py-5 border-b bg-green-50 shrink-0">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-cover bg-center pointer-events-none"
                        style={{ backgroundImage: `url(${SidebarHeaderImage})` }}
                    />
                    <div className="absolute inset-0 bg-black/35 pointer-events-none" />
                    <div className="relative z-10 flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                            <div className="bg-[#7f35b2]/30 p-2.5 rounded-xl shrink-0">
                                <FileText className="text-white" size={22} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-100">Publish Assessment</h2>
                                <p className="text-sm text-gray-100 mt-0.5">Share your assessment with respondents and start collecting responses.</p>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-gray-200/60 transition text-gray-100"
                            aria-label="Close"
                        >
                            <X size={20} strokeWidth={2} />
                        </button>
                    </div>
                </div>

                <div className="relative flex-1 overflow-hidden">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-right-bottom opacity-[0.07] pointer-events-none"
                        style={{ backgroundImage: `url(${sidebarImage})`, backgroundSize: "360px auto" }}
                    />

                    <div className="relative h-full overflow-y-auto p-5 md:p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex items-center justify-center w-12 h-12 bg-[#c2a8f026] rounded-full relative shrink-0">
                                <FileText size={20} className="text-[#7f35b2]" strokeWidth={2} />
                                <div className="absolute bottom-0 right-0 bg-white rounded-full p-0.5">
                                    <div className="bg-[#c2a8f04d] rounded-full p-1">
                                        <div className="w-1.5 h-1.5 bg-[#c2a8f026]0 rounded-full"></div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-800">
                                    Publish Assessment
                                </h2>
                                <p className="text-gray-500 text-xs mt-0.5">
                                    Share your assessment with respondents and start collecting responses.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-3 flex-1">
                            <div>
                                <label className="block text-xs font-bold text-slate-800 mb-1">
                                    Thank you note <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows={4}
                                    value={form.thankYouNote}
                                    onChange={(e) => setField("thankYouNote", e.target.value)}
                                    maxLength={500}
                                    placeholder="Shown to respondents after they submit"
                                    className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:border-transparent outline-none transition text-sm ${errors.thankYouNote ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-[#c2a8f033]"}`}
                                />
                                <div className="flex justify-between items-start mt-1">
                                    <div>
                                        {errors.thankYouNote && (
                                            <p className="flex items-center gap-1 text-[10px] text-red-600">
                                                <AlertCircle size={10} />
                                                {errors.thankYouNote}
                                            </p>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-gray-400">
                                        {form.thankYouNote.length} / 500
                                    </p>
                                </div>
                            </div>

                            <div className="border border-gray-200 rounded-xl p-4 bg-white">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="bg-[#c2a8f026] p-1.5 rounded-md">
                                            <Calendar size={16} className="text-[#7f35b2]" />
                                        </div>
                                        <h3 className="text-sm font-bold text-slate-800 border-l-2 border-[#c2a8f04d] pl-2">Schedule</h3>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={form.startImmediately}
                                                onChange={(e) => setField("startImmediately", e.target.checked)}
                                                className="w-3.5 h-3.5 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2] cursor-pointer"
                                            />
                                            Start immediately
                                        </label>
                                        <Info size={14} className="text-gray-400" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
                                    <div className="hidden md:block absolute left-1/2 top-0 bottom-0 border-l border-dashed border-gray-200"></div>
                                    <div className="space-y-3 md:pr-3">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-800 mb-1">
                                                Start Date <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex gap-2">
                                                <div className="relative flex-1">
                                                    <input
                                                        type="date"
                                                        min={getTodayDateString()}
                                                        value={form.startDate}
                                                        disabled={form.startImmediately}
                                                        onChange={(e) => setField("startDate", e.target.value)}
                                                        className={`w-full border rounded-lg pl-3 pr-8 py-1.5 text-sm outline-none transition disabled:bg-gray-50 disabled:text-gray-400 focus:ring-2 focus:border-transparent ${errors.startDate ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-[#c2a8f033]"
                                                            }`}
                                                    />
                                                </div>
                                                <div className="relative flex-1">
                                                    <input
                                                        type="time"
                                                        step={1}
                                                        value={form.startTime}
                                                        disabled={form.startImmediately}
                                                        onChange={(e) => setField("startTime", e.target.value)}
                                                        className={`w-full border rounded-lg pl-3 pr-8 py-1.5 text-sm outline-none transition disabled:bg-gray-50 disabled:text-gray-400 focus:ring-2 focus:border-transparent ${errors.startTime ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-[#c2a8f033]"
                                                            }`}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-800 mb-1">
                                                End Date <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex gap-2">
                                                <div className="relative flex-1">
                                                    <input
                                                        type="date"
                                                        value={form.endDate}
                                                        min={form.startImmediately ? getTodayDateString() : (form.startDate || getTodayDateString())}
                                                        onChange={(e) => setField("endDate", e.target.value)}
                                                        className={`w-full border rounded-lg pl-3 pr-8 py-1.5 text-sm outline-none transition focus:ring-2 focus:border-transparent ${errors.endDate ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-[#c2a8f033]"
                                                            }`}
                                                    />
                                                </div>
                                                <div className="relative flex-1">
                                                    <input
                                                        type="time"
                                                        step={1}
                                                        value={form.endTime}
                                                        onChange={(e) => setField("endTime", e.target.value)}
                                                        className={`w-full border rounded-lg pl-3 pr-8 py-1.5 text-sm outline-none transition focus:ring-2 focus:border-transparent ${errors.endTime ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-[#c2a8f033]"
                                                            }`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3 md:pl-3">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-800 mb-1">
                                                Part A Time (min) <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={form.partATime}
                                                    onChange={(e) => setField("partATime", e.target.value)}
                                                    placeholder="e.g. 30"
                                                    className={`w-full border rounded-lg pl-3 pr-8 py-1.5 text-sm outline-none transition focus:ring-2 focus:border-transparent ${errors.partATime ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-[#c2a8f033]"
                                                        }`}
                                                />
                                                <Clock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7f35b2]" />
                                            </div>
                                            {errors.partATime && (
                                                <p className="flex items-center gap-1 text-[10px] text-red-600 mt-1">
                                                    <AlertCircle size={10} />
                                                    {errors.partATime}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-800 mb-1">
                                                Part B Time (min) <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={form.partBTime}
                                                    onChange={(e) => setField("partBTime", e.target.value)}
                                                    placeholder="e.g. 30"
                                                    className={`w-full border rounded-lg pl-3 pr-8 py-1.5 text-sm outline-none transition focus:ring-2 focus:border-transparent ${errors.partBTime ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-[#c2a8f033]"
                                                        }`}
                                                />
                                                <Clock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7f35b2]" />
                                            </div>
                                            {errors.partBTime && (
                                                <p className="flex items-center gap-1 text-[10px] text-red-600 mt-1">
                                                    <AlertCircle size={10} />
                                                    {errors.partBTime}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 mt-4 pt-2">
                            <button
                                onClick={onClose}
                                disabled={submitting}
                                className="px-5 py-2 text-sm text-slate-700 bg-gray-100 font-bold hover:bg-gray-200 rounded-lg transition disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePublish}
                                disabled={submitting}
                                className="flex items-center gap-1.5 px-5 py-2 text-sm bg-[#7f35b2] hover:bg-[#3bb290] text-white font-bold rounded-lg transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <Send size={14} />
                                {submitting ? "Publishing..." : "Publish Assessment"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PublishSurveyModal;
